import type { Portfolio } from '../../api/portfolio-client/portfolio-client.type';
import type { ProjectsResponse } from '../../api/projects-client/projects-client.type';
import { FloraSupportedObjectTypes, type FloraContextualInfo } from '../flora/flora.type';
import { GeneralViewNames } from '../side-bar/side-bar.type';
import { AgentMinerTab } from './agent-miner.type';
import { MODEL_PROVIDER_VALUES } from './model-comparison/model-comparison.type';
import {
  DEFAULT_TASK_FIT_DIMENSION,
  DEFAULT_TASK_FIT_METRIC,
  TASK_FIT_DIMENSION_VALUES,
  TASK_FIT_METRIC_VALUES,
} from './task-fit/task-fit.type';
import { DEFAULT_USAGE_MEASURE, USAGE_MEASURE_VALUES } from './usage/usage.type';
import { readFiltersFromSearch, resolveStartEnd } from './sessions-list/filters/session-filters.helpers';

type AgentMinerFloraContextOptions = {
  activeTab: AgentMinerTab;
  portfolio: Portfolio | null | undefined;
  teams: ProjectsResponse[];
  startDate: string;
  endDate: string;
  allocation: string | undefined;
  searchParams: URLSearchParams;
};

/** Maximum length for a URL-derived display value included in Flora's prompt context. */
const MAX_CONTEXT_VALUE_LENGTH = 120;
const MAX_CONTEXT_FILTER_VALUES = 10;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Returns a bounded, single-line UI value suitable for descriptive prompt context.
 *
 * @param {string | null} value - Raw URL-derived value, or null when the param is absent
 * @returns {string | null} The whitespace-collapsed value, or null when empty or longer than {@link MAX_CONTEXT_VALUE_LENGTH}
 */
function getContextValue(value: string | null): string | null {
  if (!value) return null;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized || normalized.length > MAX_CONTEXT_VALUE_LENGTH) return null;
  return normalized;
}

/**
 * Returns an allowed comma-separated Model Provider selection from the URL.
 *
 * @param {string | null} value - Comma-separated provider list from the URL, or null when absent
 * @returns {string[]} De-duplicated, lower-cased providers restricted to {@link MODEL_PROVIDER_VALUES}
 */
function getProviders(value: string | null): string[] {
  const providers =
    value
      ?.split(',')
      .map((provider) => provider.toLowerCase())
      .filter(Boolean) ?? [];
  return Array.from(new Set(providers)).filter((provider) => MODEL_PROVIDER_VALUES.has(provider));
}

/**
 * Returns a date only when it matches the UI's wire-format date representation.
 *
 * @param {string | null} value - Candidate date string from the URL, or null when absent
 * @returns {string | null} The value when it is a `YYYY-MM-DD` string, otherwise null
 */
function getIsoDate(value: string | null): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

/**
 * Returns a bounded set of display values from URL-backed filters.
 *
 * @param {string[]} values - Raw filter values to normalize and bound
 * @returns {string[]} De-duplicated valid values, capped at {@link MAX_CONTEXT_FILTER_VALUES}
 */
function getContextValues(values: string[]): string[] {
  return Array.from(
    new Set(values.map((value) => getContextValue(value)).filter((value): value is string => !!value)),
  ).slice(0, MAX_CONTEXT_FILTER_VALUES);
}

/**
 * Adds a quoted, bounded list of descriptive filter values to prompt context.
 *
 * @param {string[]} selectedMeasures - Accumulator mutated in place with the new descriptor
 * @param {string} label - Human-readable prefix for the appended descriptor
 * @param {string[]} values - Raw filter values; bounded and quoted before appending
 * @returns {void} Nothing; `selectedMeasures` is mutated only when at least one value survives bounding
 */
function appendContextValues(selectedMeasures: string[], label: string, values: string[]): void {
  const contextValues = getContextValues(values);
  if (contextValues.length > 0)
    selectedMeasures.push(`${label}: ${contextValues.map((value) => JSON.stringify(value)).join(', ')}`);
}

/**
 * Adds an allocation descriptor only when the shared analytics scope resolved one.
 *
 * @param {string[]} selectedMeasures - Accumulator mutated in place with the new descriptor
 * @param {string | undefined} allocation - Resolved time-allocation label, or undefined when none applies
 * @returns {void} Nothing; `selectedMeasures` is mutated only when `allocation` is truthy
 */
function appendAllocation(selectedMeasures: string[], allocation: string | undefined): void {
  if (allocation) selectedMeasures.push(`Time allocation: ${allocation}`);
}

/**
 * Resolves the Model Comparison "Analysis view" descriptor for Flora context.
 *
 * For the Token-usage view the header `usage_view` toggle decides the shape:
 * the line chart reads as a trend, while the grouped bar chart and the token
 * breakdown table are breakdowns. Any other value (unknown or absent) defaults
 * to the line chart, matching the UsageView hook. Non-usage views follow the
 * sidebar `view` param.
 *
 * @param {URLSearchParams} searchParams - Current URL params, read for the `usage_view` toggle
 * @param {string | null} rawView - The sidebar `view` param, or null when absent
 * @param {boolean} isUsage - Whether the Token-usage view is active
 * @returns {string} A human-readable analysis-view descriptor (`trend`, `breakdown (table)`, `breakdown (bar chart)`, or `breakdown`)
 */
function getModelComparisonAnalysisView(
  searchParams: URLSearchParams,
  rawView: string | null,
  isUsage: boolean,
): string {
  if (isUsage) {
    const usageView = searchParams.get('usage_view');
    if (usageView === 'table') return 'breakdown (table)';
    if (usageView === 'bar') return 'breakdown (bar chart)';
    return 'trend';
  }
  return rawView === 'chart' || rawView === null ? 'trend' : 'breakdown';
}

/**
 * Builds the selected-measure descriptors for Model Comparison and Token Usage.
 *
 * @param {URLSearchParams} searchParams - Current URL params describing the active view, measure, providers, and drill-down
 * @param {string | undefined} allocation - Resolved time-allocation label, or undefined when none applies
 * @returns {string[]} Ordered descriptor lines summarizing the current Model Comparison / Token Usage scope
 */
function getModelComparisonMeasures(searchParams: URLSearchParams, allocation: string | undefined): string[] {
  const rawView = searchParams.get('view');
  const isUsage = rawView === 'usage';
  const selectedMeasures = [
    `Agent Miner domain: ${isUsage ? 'usage' : 'models'}`,
    `Analysis view: ${getModelComparisonAnalysisView(searchParams, rawView, isUsage)}`,
    'Analytics scope: organization-wide',
  ];
  appendAllocation(selectedMeasures, allocation);

  if (isUsage) {
    const rawMeasure = searchParams.get('measure');
    const measure = rawMeasure && USAGE_MEASURE_VALUES.has(rawMeasure) ? rawMeasure : DEFAULT_USAGE_MEASURE;
    selectedMeasures.push(`Token measure: ${measure}`);
  }

  const providers = getProviders(searchParams.get('model_provider'));
  if (providers.length > 0) selectedMeasures.push(`Visible model providers: ${providers.join(', ')}`);

  const hiddenFamilies = searchParams.get('hidden_families')?.split(',').filter(Boolean).length ?? 0;
  if (hiddenFamilies > 0) selectedMeasures.push(`Hidden model families: ${hiddenFamilies}`);
  if (searchParams.get('model_show_unknown') === '1') selectedMeasures.push('Unknown model family is visible');

  const selectedFamily = getContextValue(searchParams.get('sessions_family'));
  if (selectedFamily) selectedMeasures.push(`Selected model family: ${JSON.stringify(selectedFamily)}`);

  const drilldownStart = getIsoDate(searchParams.get('sessions_start'));
  const drilldownEnd = getIsoDate(searchParams.get('sessions_end'));
  if (selectedFamily && drilldownStart && drilldownEnd) {
    selectedMeasures.push(`Selected family date range: ${drilldownStart} to ${drilldownEnd}`);
  }

  return selectedMeasures;
}

/**
 * Builds the selected-measure descriptors for Delivery Impact.
 *
 * @param {URLSearchParams} searchParams - Current URL params describing the Delivery Impact view and cohort
 * @param {string | undefined} allocation - Resolved time-allocation label, or undefined when none applies
 * @returns {string[]} Ordered descriptor lines summarizing the current Delivery Impact scope
 */
function getDeliveryImpactMeasures(searchParams: URLSearchParams, allocation: string | undefined): string[] {
  const isTrend = searchParams.get('di_view') === 'trend';
  const selectedMeasures = [
    'Agent Miner domain: delivery',
    `Analysis view: ${isTrend ? 'trend' : 'summary'}`,
    'Analytics scope: organization-wide',
  ];
  appendAllocation(selectedMeasures, allocation);
  if (searchParams.get('di_panel') === 'ai') selectedMeasures.push('Selected cohort: AI-assisted sessions');
  return selectedMeasures;
}

/**
 * Builds the selected-measure descriptors for Task Fit and its cell drill-down.
 *
 * @param {URLSearchParams} searchParams - Current URL params describing the dimension, metric, providers, and selected cell
 * @param {string | undefined} allocation - Resolved time-allocation label, or undefined when none applies
 * @returns {string[]} Ordered descriptor lines summarizing the current Task Fit scope
 */
function getTaskFitMeasures(searchParams: URLSearchParams, allocation: string | undefined): string[] {
  const rawDimension = searchParams.get('taskfit_dimension');
  const dimension =
    rawDimension && TASK_FIT_DIMENSION_VALUES.has(rawDimension) ? rawDimension : DEFAULT_TASK_FIT_DIMENSION;
  const rawMetric = searchParams.get('taskfit_metric');
  const metric = rawMetric && TASK_FIT_METRIC_VALUES.has(rawMetric) ? rawMetric : DEFAULT_TASK_FIT_METRIC;
  const selectedMeasures = [
    'Agent Miner domain: task_fit',
    'Analysis view: breakdown',
    'Analytics scope: organization-wide',
    `Task-fit dimension: ${dimension}`,
    `Task-fit metric: ${metric}`,
  ];
  appendAllocation(selectedMeasures, allocation);

  const providers = getProviders(searchParams.get('taskfit_model_provider'));
  if (providers.length > 0) selectedMeasures.push(`Visible model providers: ${providers.join(', ')}`);
  const hiddenFamilies = searchParams.get('taskfit_hidden_families')?.split(',').filter(Boolean).length ?? 0;
  if (hiddenFamilies > 0) selectedMeasures.push(`Hidden model families: ${hiddenFamilies}`);
  if (searchParams.get('taskfit_show_unknown') === '1') selectedMeasures.push('Unknown model family is visible');

  const rowKey = getContextValue(searchParams.get('taskfit_cell_row'));
  const modelFamily = getContextValue(searchParams.get('taskfit_cell_family'));
  if (rowKey && modelFamily) {
    selectedMeasures.push(
      `Selected task-fit cell: row ${JSON.stringify(rowKey)}, model family ${JSON.stringify(modelFamily)}`,
    );
  }
  return selectedMeasures;
}

/**
 * Builds the selected-measure descriptors for Adoption.
 *
 * @param {URLSearchParams} searchParams - Current URL params describing the Adoption view and measure
 * @param {string | undefined} allocation - Resolved time-allocation label, or undefined when none applies
 * @returns {string[]} Ordered descriptor lines summarizing the current Adoption scope
 */
function getAdoptionMeasures(searchParams: URLSearchParams, allocation: string | undefined): string[] {
  const view = searchParams.get('ad_view');
  const selectedMeasures = [
    'Agent Miner domain: adoption',
    `Analysis view: ${view === 'trends' || view === null ? 'trend' : 'breakdown'}`,
  ];
  appendAllocation(selectedMeasures, allocation);
  if (view === 'trends' || view === null) {
    const rawMeasure = searchParams.get('ad_measure');
    const measure = rawMeasure === 'sessions' || rawMeasure === 'adoption_rate' ? rawMeasure : 'active_users';
    selectedMeasures.push(`Adoption measure: ${measure}`);
  }
  return selectedMeasures;
}

/**
 * Builds the contextual description of the currently filtered Sessions list.
 *
 * @param {URLSearchParams} searchParams - Current URL params describing the session filters (date range, search, sources, owners, labels, model families, association, empties)
 * @returns {Pick<FloraContextualInfo, 'startDate' | 'endDate' | 'selectedMeasures'>} The resolved date window and ordered descriptor lines for the filtered Sessions list
 */
function getSessionsContext(
  searchParams: URLSearchParams,
): Pick<FloraContextualInfo, 'startDate' | 'endDate' | 'selectedMeasures'> {
  const filters = readFiltersFromSearch(searchParams);
  const { start, end } = resolveStartEnd(filters);
  const dateRangeLabels = {
    all: 'All time',
    '24h': 'Last 24 hours',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    custom: 'Custom range',
  } as const;
  const selectedMeasures = [
    'Agent Miner view: sessions',
    'Session scope: organization-wide',
    `Session date filter: ${dateRangeLabels[filters.dateRange]}`,
  ];

  const query = getContextValue(filters.q);
  if (query) selectedMeasures.push(`Session search text: ${JSON.stringify(query)}`);
  appendContextValues(selectedMeasures, 'Session source filters', filters.sources);
  appendContextValues(
    selectedMeasures,
    'Session owner IDs',
    filters.userIds.filter((userId) => UUID_PATTERN.test(userId)),
  );
  appendContextValues(selectedMeasures, 'Session label filters', filters.labels);
  appendContextValues(selectedMeasures, 'Model-family display filters', filters.modelFamilies);
  if (filters.hasTask === true) selectedMeasures.push('Session association: task-linked only');
  if (filters.hasTask === false) selectedMeasures.push('Session association: no linked task only');
  if (filters.hideEmpty) selectedMeasures.push('Empty sessions are hidden (minimum one turn)');

  return { startDate: start ?? null, endDate: end ?? null, selectedMeasures };
}

/**
 * Builds the existing Flora contextual-info payload for an active Agent Miner
 * AI Insights tab. The backend formats these values into the system prompt, so
 * they describe the current screen scope without serializing dashboard data.
 *
 * @param {AgentMinerFloraContextOptions} options - The active tab, portfolio/teams for object context, the shared start/end dates and allocation, and the current URL params
 * @returns {FloraContextualInfo | null} The contextual-info payload for the active tab (currently non-null for every handled tab)
 */
function getAgentMinerFloraContext({
  activeTab,
  portfolio,
  teams,
  startDate,
  endDate,
  allocation,
  searchParams,
}: AgentMinerFloraContextOptions): FloraContextualInfo | null {
  let selectedMeasures: string[];
  let contextualStartDate: string | null = startDate;
  let contextualEndDate: string | null = endDate;
  switch (activeTab) {
    case AgentMinerTab.ModelComparison:
      selectedMeasures = getModelComparisonMeasures(searchParams, allocation);
      break;
    case AgentMinerTab.DeliveryImpact:
      selectedMeasures = getDeliveryImpactMeasures(searchParams, allocation);
      break;
    case AgentMinerTab.TaskFit:
      selectedMeasures = getTaskFitMeasures(searchParams, allocation);
      break;
    case AgentMinerTab.Adoption:
      selectedMeasures = getAdoptionMeasures(searchParams, allocation);
      break;
    case AgentMinerTab.Sessions: {
      const sessionsContext = getSessionsContext(searchParams);
      selectedMeasures = sessionsContext.selectedMeasures ?? [];
      contextualStartDate = sessionsContext.startDate;
      contextualEndDate = sessionsContext.endDate;
      break;
    }
  }

  const selectedTeamIds = new Set(searchParams.get('ad_teams')?.split(',').filter(Boolean) ?? []);
  const selectedTeams = teams.filter((team) => selectedTeamIds.has(team.id));
  const currentObjects =
    activeTab === AgentMinerTab.Adoption
      ? [
          ...(portfolio ? [{ type: FloraSupportedObjectTypes.Portfolio, id: portfolio.id, name: portfolio.name }] : []),
          ...selectedTeams.map((team) => ({ type: FloraSupportedObjectTypes.Project, id: team.id, name: team.name })),
        ]
      : [];

  return {
    currentModule: GeneralViewNames.AgentMiner,
    currentObjects,
    startDate: contextualStartDate,
    endDate: contextualEndDate,
    selectedMeasures,
  };
}

export { getAgentMinerFloraContext };
export type { AgentMinerFloraContextOptions };
